import { nextTick } from 'vue'

export const getWidgetScrollContainer = (): HTMLElement | null => {
  return document.getElementById('core-widget-scroll')
}

export const scrollToElement = (element: Element) => {
  const scrollContainer = getWidgetScrollContainer()
  if (scrollContainer) {
    const containerRect = scrollContainer.getBoundingClientRect()
    const elementRect = element.getBoundingClientRect()
    const targetTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - 32

    scrollContainer.scrollTo({
      top: Math.max(0, targetTop),
      left: 0,
      behavior: 'smooth',
    })
    return
  }

  window.scrollTo({
    top: window.scrollY + element.getBoundingClientRect().y - 79,
    left: 0,
    behavior: 'smooth',
  })
}

export const scrollToSection = async (sectionID: string) => {
  await nextTick(() => {
    const element = document.querySelector(sectionID)
    if (element) {
      scrollToElement(element)
    }
  })
}

export function getPartTitle(part: string, wt: (...path: string[]) => string): string {
  if (part === 'general') {
    return wt('navigation', 'general')
  }
  const segments = part.split('_')
  const stage = segments.pop()
  const role = segments.join('_')

  if (!stage || !role) {
    return part
  }

  if (stage === 'office') {
    return wt('navigation', 'office')
  }
  return `${wt('navigation', stage)} ${wt('roleParts', role.toUpperCase())}`
}

export type SupportedWidgetLanguage = 'de-CH' | 'en-GB' | 'fr-FR' | 'it-IT'

export const LANGUAGE_STORAGE_KEY = 'core-widget-language'
export const SUPPORTED_WIDGET_LANGUAGES: SupportedWidgetLanguage[] = ['de-CH', 'en-GB', 'fr-FR', 'it-IT']
const DEFAULT_WIDGET_LANGUAGE: SupportedWidgetLanguage = 'de-CH'

export const validateLanguage = (lang: string | null): string => {
  if (!lang) {
    return ''
  }

  const normalizedLang = lang.trim()
  const languageMap: Record<SupportedWidgetLanguage, string[]> = {
    'de-CH': ['de-CH', 'DE', 'de'],
    'en-GB': ['en-GB', 'EN', 'en'],
    'fr-FR': ['fr-FR', 'FR', 'fr'],
    'it-IT': ['it-IT', 'IT', 'it'],
  }

  const language = SUPPORTED_WIDGET_LANGUAGES.find((locale) => {
    return languageMap[locale].some((pattern) => pattern.toLowerCase() === normalizedLang.toLowerCase())
  })

  return language || ''
}

export const normalizeAvailableLanguages = (languages: string[] | null | undefined): SupportedWidgetLanguage[] => {
  if (!Array.isArray(languages) || languages.length === 0) {
    return [...SUPPORTED_WIDGET_LANGUAGES]
  }

  const availableLanguages = new Set<SupportedWidgetLanguage>()

  languages.forEach((language) => {
    const normalized = validateLanguage(language)
    if (normalized) {
      availableLanguages.add(normalized as SupportedWidgetLanguage)
    }
  })

  const resolved = SUPPORTED_WIDGET_LANGUAGES.filter((language) => availableLanguages.has(language))

  return resolved.length > 0 ? resolved : [...SUPPORTED_WIDGET_LANGUAGES]
}

export const resolveAllowedLanguage = (
  preferred: string | null | undefined,
  availableLanguages: SupportedWidgetLanguage[],
  fallback?: string | null | undefined
): SupportedWidgetLanguage => {
  const allowedLanguages = availableLanguages.length > 0 ? availableLanguages : [...SUPPORTED_WIDGET_LANGUAGES]

  const normalizedPreferred = validateLanguage(preferred ?? null)
  if (normalizedPreferred && allowedLanguages.includes(normalizedPreferred as SupportedWidgetLanguage)) {
    return normalizedPreferred as SupportedWidgetLanguage
  }

  const normalizedFallback = validateLanguage(fallback ?? null)
  if (normalizedFallback && allowedLanguages.includes(normalizedFallback as SupportedWidgetLanguage)) {
    return normalizedFallback as SupportedWidgetLanguage
  }

  return allowedLanguages[0] || DEFAULT_WIDGET_LANGUAGE
}

export const areLanguagesEquivalent = (left: string | null, right: string | null): boolean => {
  if (!left || !right) {
    return false
  }

  const leftCode = left.split('-')[0]?.toLowerCase()
  const rightCode = right.split('-')[0]?.toLowerCase()

  return !!leftCode && leftCode === rightCode
}
